import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './UserInfo.css';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import Button from '../Button/Button';
import { convertApiRoleToUiRole, isPrestataireLibreRole } from '../../utils/roleUtils';
import { useProfile } from '../../contexts/ProfileContext';
import { useAcheteurProfile } from '../../contexts/AcheteurProfileContext';
import { useNotifications } from '../../contexts/NotificationsContextType';
import { useGuidePrincipalUrl } from '../../hooks/useGuidePrincipalUrl';

// Raison du grisage des entrees reservees aux titulaires (Chiffrage, Commandes) dans la navigation
// rapide. Definie une fois : les deux entrees affichent le meme motif, et deux libelles recopies
// finiraient par diverger.
const RESERVE_TITULAIRES = 'Réservé aux prestataires titulaires';

const getContactUpdateRoute = (role: string) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  switch (role) {
    case 'Acheteur signataire admin':
      return `${apiUrl}/responsables-achat/me`;
    case 'Acheteur signataire':
      return `${apiUrl}/validateurs-achat/me`;
    case 'Acheteur':
      return `${apiUrl}/acheteurs/me`;
    case 'Prestataire signataire admin':
      return `${apiUrl}/gestionnaires/me`;
    case 'Prestataire signataire':
      return `${apiUrl}/signataires/me`;
    case 'Prestataire':
      return `${apiUrl}/interlocuteurs/me`;
    // Format UI (avec espace)
    case 'Prestataire libre admin':
    case 'Prestataire libre':
    // Format API (avec underscore) - CORE-21
    case 'Prestataire_Libre':
    case 'Prestataire_Libre_Admin':
      return `${apiUrl}/prestataires-libres/me`;
    default:
      return '';
  }
};

const MissingInfoPopup = ({ isOpen, userInfo, onSubmit }) => {
  const safeUser = userInfo || {};

  const [form, setForm] = useState({
    nom: safeUser.nom === "Non renseigne" ? "" : (safeUser.nom || ""),
    prenom: safeUser.prenom === "Non renseigne" ? "" : (safeUser.prenom || ""),
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-articles-overlay">
      <div className="modal-articles">
        <div className="modal-articles-header">
          <h2>Complétez vos informations</h2>
        </div>

        <div className="modal-articles-content" style={{ padding: "2rem" }}>
          <p style={{ marginBottom: "2rem" }}>Merci de renseigner les informations manquantes :</p>

          <div className="form-group">
            <label>Nom <span style={{ color: 'red' }}>*</span></label>
            <input
              name="nom"
              value={form.nom}
              onChange={handleChange}
              placeholder="Nom"
              required
            />
          </div>

          <div className="form-group">
            <label>Prénom <span style={{ color: 'red' }}>*</span></label>
            <input
              name="prenom"
              value={form.prenom}
              onChange={handleChange}
              placeholder="Prénom"
              required
            />
          </div>

          <Button
            onClick={() => onSubmit(form)}
            value="Valider"
            disabled={!form.nom.trim() || !form.prenom.trim()}
          />

        </div>
      </div>
    </div>
  );
};

const DemoButton: React.FC<{ token: string | null }> = ({ token }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 10000);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/demo/bridge-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });
      const raw = await res.text();
      let data: any = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        data = null;
      }

      if (data.status === 'success' && data.data?.redirect_url) {
        window.location.href = data.data.redirect_url;
      } else {
        setError(data?.message || 'Demo indisponible temporairement.');
      }
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        setError('Timeout demo. Réessayez dans quelques secondes.');
      } else {
        setError('Connexion demo impossible pour le moment.');
      }
    } finally {
      window.clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  return (
    <div>
      {/* <button
        className="user-info-demo-btn"
        onClick={handleClick}
        disabled={loading}
        title="Tester le module Publications en mode démo sans impact réel"
      >
        {loading ? '...' : 'Tester sur Démo'}
      </button>
      {error && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{error}</div>} */}
    </div>
  );
};

type NavItem = {
  key: string;
  label: string;
  to?: string;
  disabled?: boolean;
  title?: string;       // infobulle (survol) quand l'item est desactive
  count?: number;       // pastille de comptage (notifications)
  completion?: number;  // pastille de completion profil (%)
};

interface UserInfoProps {
  /** Mode top-nav : la navigation (rapide + menu deroulant) vit dans la navbar (sidebar desactivee). */
  topNav?: boolean;
}

const UserInfo: React.FC<UserInfoProps> = ({ topNav = false }) => {
  const { user, logout } = useAuth();
  // Meme guide que le footer : celui marque « principal » dans le BO (source partagee, une seule requete).
  // Appele ICI, avec les autres hooks : plus bas le composant a des retours anticipes (deconnecte,
  // profil en chargement, visiteur) et un hook place apres eux change le nombre de hooks d'un rendu a
  // l'autre, ce que React sanctionne par une exception (page 500).
  const guideUrl = useGuidePrincipalUrl();
  const [userInfo, setUserInfo] = useState<{ nom?: string; email_contact?: string; prenom?: string; organisation?: string; role?: string } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [showPopup, setShowPopup] = useState(false);
  // Soulignement glissant de la nav rapide : un seul indicateur qui se deplace vers l'item actif.
  const quicknavRef = useRef<HTMLElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  // Completion (pastille avatar/menu) + compteurs : meme source que la sidebar, selon le role.
  const { completion, refreshCompletion } = useProfile?.() || { completion: null, refreshCompletion: undefined };
  const { completion: acheteurCompletion, refreshCompletion: refreshAcheteurCompletion } = useAcheteurProfile?.() || { completion: null, refreshCompletion: undefined };
  const { refresh: refreshNotifications, unreadCommandes = 0, unreadOperations = 0, unreadCandidatures = 0 } = useNotifications() || {};

  const isAcheteur = !!user?.role && ['Acheteur', 'Acheteur signataire', 'Acheteur signataire admin'].includes(user.role);
  // Prestataire libre (sans contrat) : pas de commandes -> bouton Commandes desactive dans la nav.
  const isPrestataireLibre = !!user?.role && isPrestataireLibreRole(user.role);

  // La sidebar portait le refresh des pastilles. En mode top-nav elle est desactivee :
  // on rejoue ici la meme logique (evenements d'edition + a chaque navigation).
  useEffect(() => {
    if (!refreshCompletion) return;
    const h = () => refreshCompletion();
    window.addEventListener('profile-updated', h);
    return () => window.removeEventListener('profile-updated', h);
  }, [refreshCompletion]);

  useEffect(() => {
    if (!refreshAcheteurCompletion) return;
    const h = () => refreshAcheteurCompletion();
    window.addEventListener('acheteur-profile-updated', h);
    return () => window.removeEventListener('acheteur-profile-updated', h);
  }, [refreshAcheteurCompletion]);

  useEffect(() => {
    if (!user) return;
    refreshNotifications?.();
    if (isAcheteur) refreshAcheteurCompletion?.();
    else if (user.role) refreshCompletion?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user, isAcheteur]);

  const updateUserInfo = async (updated) => {
    if (!user?.role) return;
    const route = `${process.env.REACT_APP_API_URL}/users/me`;
    if (!route) return;

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(route, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          nom: updated.nom,
          prenom: updated.prenom,
        })
      });

      if (response.ok) {
        const data = await response.json();
        setUserInfo({
          ...userInfo,
          nom: data.data[0].nom,
          prenom: data.data[0].prenom,
        });
      }
    } catch (e) {
      setUserInfo({});
    }
  }

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!user?.role) return;
      const route = getContactUpdateRoute(user.role);
      if (!route) return;

      const token = localStorage.getItem('token');

      try {
        const response = await fetch(route, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          logout();
          navigate('/login');
          return;
        }

        if (response.status === 404) {
          const errorData = await response.json();
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          logout();
          navigate('/login', { state: { error: errorData.message || 'Utilisateur non trouvé' } });
          return;
        }

        if (!response.ok) {
          const errorData = await response.json();
          // Compte parque dans l'entite par defaut : le serveur refuse (403 ENTITE_PAR_DEFAUT) mais la
          // session est valide. Ne pas deconnecter : MainLayout affiche l'ecran bloquant oriente support.
          if (errorData?.code === 'ENTITE_PAR_DEFAUT') {
            return;
          }
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          logout();
          navigate('/login', { state: { error: errorData.message || 'Une erreur est survenue' } });
          return;
        }

        if (response.ok) {
          const data = await response.json();

          const infos = {
            nom: data.data.nom,
            prenom: data.data.prenom,
            organisation: data.data.organisme?.nom ?? data.data.entite?.nom,
            role: user.role,
            email_contact: data.data.email_contact
          };

          setUserInfo(infos);

          const missing =
            !infos.nom || infos.nom === "Non renseigne" ||
            !infos.prenom || infos.prenom === "Non renseigne" ||
            !infos.email_contact || infos.email_contact === "Non renseigne";

          if (missing) {
            setShowPopup(true);
          }
        }
      } catch (e) {
        setUserInfo({});
      }
    };

    fetchUserInfo();
  }, [user?.role, navigate, logout]);

  // Soulignement glissant : (re)mesure l'item actif a chaque changement de route, au montage
  // de la quicknav (apres chargement de l'utilisateur / de l'identite) ET a chaque reflow de la
  // nav (apparition de la scrollbar, resize) via ResizeObserver. Positions en coordonnees nav
  // (getBoundingClientRect) pour rester aligne quelles que soient les marges.
  useLayoutEffect(() => {
    const nav = quicknavRef.current;
    if (!nav) return;

    const measure = () => {
      const active = nav.querySelector<HTMLElement>('.quicknav-item.active');
      if (!active) { setIndicator(null); return; }
      const navLeft = nav.getBoundingClientRect().left;
      const box = active.getBoundingClientRect();
      setIndicator({ left: box.left - navLeft, width: box.width });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [location.pathname, topNav, user?.role, userInfo]);

  // Nav rapide (items du centre) : depend du role + compteurs, PAS de userInfo -> on peut
  // l'afficher reelle des le chargement (pas de skeleton sur ces items).
  const commandesTo = isAcheteur ? '/operations' : '/commandes';
  const commandesCount = isAcheteur ? unreadOperations : unreadCommandes;

  // Pastille count (> 0) ou completion (< 100) selon l'item.
  const renderBadge = (item: NavItem) => {
    if (typeof item.completion === 'number' && item.completion < 100) {
      return <span className="nav-badge nav-badge--completion">{item.completion}%</span>;
    }
    if (item.count && item.count > 0) {
      return <span className="nav-badge">{item.count > 99 ? '99+' : item.count}</span>;
    }
    return null;
  };

  // Annuaire : chaque profil voit l'autre partie en priorite (acheteur -> prestataires, presta -> acheteurs).
  const annuaireTo = isAcheteur ? '/annuaire?tab=prestataires' : '/annuaire?tab=acheteurs';
  // Un prestataire LIBRE n'est titulaire d'aucun lot : il n'a ni commande ni chiffrage a etablir
  // (un chiffrage se compose sur les prix en vigueur d'un couple lot/departement dont on est
  // titulaire). Les deux entrees sont donc grisees avec la MEME raison — le message est defini une
  // fois, deux copies auraient fini par diverger.
  const quickItems: NavItem[] = [
    isAcheteur
      ? { key: 'catalogue', label: 'Catalogue', to: '/catalogue' }
      : isPrestataireLibre
        ? { key: 'chiffrage', label: 'Chiffrage', disabled: true, title: RESERVE_TITULAIRES }
        : { key: 'chiffrage', label: 'Chiffrage', to: '/chiffrage' },
    isPrestataireLibre
      ? { key: 'commandes', label: 'Commandes', disabled: true, title: RESERVE_TITULAIRES }
      : { key: 'commandes', label: 'Commandes', to: commandesTo, count: commandesCount },
    { key: 'annuaire', label: 'Annuaire', to: annuaireTo },
    ...(!isAcheteur ? [{ key: 'publications', label: 'Publications', to: '/publications', count: unreadCandidatures }] : []),
  ];

  // Item actif : la page courante, ET pour Commandes aussi les pages de suivi
  // (operation/commande detail : /operation-* , /commande-*).
  const isItemActive = (item: NavItem): boolean => {
    if (!item.to) return false;
    const path = location.pathname;
    // item.to peut porter une query (?tab=) : on ne compare que le pathname.
    const toPath = item.to.split('?')[0];
    if (item.key === 'commandes') {
      return path === toPath || /^\/(commande|operation)-/.test(path);
    }
    return path === toPath || path.startsWith(toPath + '/');
  };

  const quicknavEl = topNav ? (
    <nav className="navbar-quicknav" aria-label="Navigation rapide" ref={quicknavRef}>
      {quickItems.map((item) =>
        item.disabled || !item.to ? (
          <span key={item.key} className="quicknav-item quicknav-item--disabled" title={item.title ?? 'Bientôt disponible'}>
            {item.label}
          </span>
        ) : (
          <NavLink key={item.key} to={item.to} className={`quicknav-item${isItemActive(item) ? ' active' : ''}`}>
            {item.label}
            {renderBadge(item)}
          </NavLink>
        )
      )}
      {indicator && (
        <span
          className="quicknav-indicator"
          style={{ transform: `translateX(${indicator.left}px)`, width: `${indicator.width}px` }}
        />
      )}
    </nav>
  ) : null;

  // Deconnecte : bandeau gris (comme connecte) avec un bouton Connexion, sans nav rapide ni skeleton.
  if (!user) {
    return (
      <div className="user-info-display">
        <div className="user-info-right">
          <div className="user-info-auth">
            <button type="button" className="user-info-auth-link" onClick={() => navigate('/login')}>
              Connexion
            </button>
            <span className="user-info-auth-sep">/</span>
            <button type="button" className="user-info-auth-link" onClick={() => navigate('/register')}>
              Inscription
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (userInfo === null) {
    // Items du centre reels des le chargement ; seule l'identite (a droite) est en skeleton.
    return (
      <div className="user-info-display">
        {quicknavEl}
        <div className="user-info-right" aria-hidden="true">
          <div className="user-info-text">
            <span className="nav-skel nav-skel-line nav-skel-line--name" />
            <span className="nav-skel nav-skel-line nav-skel-line--sub" />
          </div>
          <span className="nav-skel nav-skel-avatar" />
        </div>
      </div>
    );
  }

  if (Object.keys(userInfo).length === 0) {
    return (
      <div className="user-info-display">
        <div className="user-info-right">
          <div className="user-info-name">Visiteur</div>
        </div>
      </div>
    );
  }

  const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
  const fullName = `${cap(userInfo.prenom || '')} ${cap(userInfo.nom || '')}`.trim();
  const initials = (
    `${(userInfo.prenom || '')[0] || ''}${(userInfo.nom || '')[0] || ''}`
    || (userInfo.email_contact || '?')[0]
  ).toUpperCase();

  const completionPercent = (isAcheteur ? acheteurCompletion : completion)?.percent;
  const handleLogout = () => { logout(); navigate('/'); };
  // Menu deroulant = liens vers les onglets du profil de l'utilisateur (selon son role).
  // Les libelles DOIVENT correspondre EXACTEMENT aux onglets de Profil.tsx (acheteur,
  // getVisibleTabs) et ProfilUnifie.tsx (prestataire, TABS_PL/TABS_AC) : la cible
  // /profil?tab=<libelle> restaure l'onglet par correspondance exacte du nom.
  const profilTabs = isAcheteur
    ? ['Présentation', 'Documents', 'Droits', "Gestion d'équipe", 'Statistiques et besoins', 'Évaluations']
    : ['Présentation', 'Documents', 'Références', 'Bancaire', "Gestion d'équipe", 'Évaluations'];
  // Onglet de profil actif : on est sur /profil ET le ?tab= correspond (defaut = 1er onglet).
  const currentProfilTab = new URLSearchParams(location.search).get('tab');
  const isProfilTabActive = (tab: string, idx: number): boolean =>
    location.pathname === '/profil' && (currentProfilTab ? currentProfilTab === tab : idx === 0);

  return (
    <>
      <div className="user-info-display">
        {quicknavEl}

        <div className="user-info-right">
          {process.env.REACT_APP_DEMO_SITE_URL && (
            <DemoButton token={localStorage.getItem('token')} />
          )}
          <div className="user-info-identity">
            <div className="user-info-text">
              <span className="user-info-fullname">{fullName || 'Utilisateur'}</span>
              {userInfo.organisation && (
                <div className="user-info-line2">
                  <span className="user-info-org">{userInfo.organisation}</span>
                </div>
              )}
            </div>
            <div className="user-info-avatar">
              {initials}
              {typeof completionPercent === 'number' && completionPercent < 100 && (
                <span className="user-info-completion-badge">{completionPercent}%</span>
              )}
            </div>
            {topNav && (
              <svg className="user-info-chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            )}
          </div>

          {topNav && (
            <div className="user-info-menu" role="menu">
              <div
                className="user-info-menu-header"
                role="button"
                tabIndex={0}
                title="Voir mon profil"
                onClick={() => navigate('/profil')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/profil'); } }}
              >
                <div className="user-info-menu-avatar">{initials}</div>
                <div className="user-info-menu-id">
                  <span className="user-info-menu-name">{fullName || 'Utilisateur'}</span>
                  {userInfo.role && <span className="user-info-menu-role">{convertApiRoleToUiRole(userInfo.role)}</span>}
                  {userInfo.email_contact && <span className="user-info-menu-mail">{userInfo.email_contact}</span>}
                </div>
              </div>
              <div className="user-info-menu-sep" aria-hidden="true" />
              {profilTabs.map((tab, idx) => (
                <NavLink
                  key={tab}
                  to={`/profil?tab=${encodeURIComponent(tab)}`}
                  role="menuitem"
                  // className en fonction : neutralise l'auto-`active` de NavLink (qui matche le pathname
                  // /profil pour TOUS les liens ?tab=) ; on pilote l'actif via le ?tab= courant.
                  className={() => `user-info-menu-item${isProfilTabActive(tab, idx) ? ' active' : ''}`}
                >
                  <span>{tab}</span>
                </NavLink>
              ))}
              <div className="user-info-menu-sep" aria-hidden="true" />
              {/* Groupe a part, entre deux separateurs : ce n'est ni un onglet du profil ni une action de session. */}
              <a className="user-info-menu-item" href={guideUrl} target="_blank" rel="noopener noreferrer" role="menuitem">
                <span>Guide méthodologique</span>
              </a>
              <div className="user-info-menu-sep" aria-hidden="true" />
              <button type="button" className="user-info-menu-item user-info-menu-logout" onClick={handleLogout} role="menuitem">
                Déconnexion
              </button>
            </div>
          )}
        </div>
      </div>

      <MissingInfoPopup
        isOpen={showPopup}
        userInfo={userInfo}
        onSubmit={(updated) => {
          updateUserInfo(updated);
          setShowPopup(false);
        }}
      />
    </>
  );
};

export default UserInfo;
